import { AxiosError } from 'axios';
import {
  AppError,
  ErrorType,
  ToastSeverity,
  ERROR_CODES,
  DEFAULT_ERROR_MESSAGES,
} from '../types/errors';

/**
 * Service for handling error processing, categorization, and user-friendly message generation
 */
export class ErrorService {
  /**
   * Process and categorize an error from various sources
   */
  static processError(error: unknown, context?: Record<string, unknown>): AppError {
    const timestamp = new Date();
    const id = `error-${timestamp.getTime()}-${Math.random().toString(36).substr(2, 9)}`;

    // Handle Axios errors (HTTP errors)
    if (this.isAxiosError(error)) {
      return this.processAxiosError(error, id, timestamp, context);
    }

    // Handle standard JavaScript errors
    if (error instanceof Error) {
      return this.processJavaScriptError(error, id, timestamp, context);
    }

    // Handle custom error objects
    if (typeof error === 'object' && error !== null) {
      return this.processCustomError(error, id, timestamp, context);
    }

    // Handle string errors
    if (typeof error === 'string') {
      return {
        id,
        type: ErrorType.APPLICATION_ERROR,
        message: error,
        originalError: error,
        timestamp,
        context,
        severity: ToastSeverity.ERROR,
        dismissible: true,
        autoHideDuration: 6000,
      };
    }

    // Fallback for unknown error types
    return {
      id,
      type: ErrorType.UNKNOWN_ERROR,
      message: DEFAULT_ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR],
      originalError: error,
      timestamp,
      context,
      severity: ToastSeverity.ERROR,
      dismissible: true,
      autoHideDuration: 6000,
    };
  }

  /**
   * Process Axios HTTP errors
   */
  private static processAxiosError(
    error: AxiosError,
    id: string,
    timestamp: Date,
    context?: Record<string, unknown>
  ): AppError {
    const status = error.response?.status;
    const statusText = error.response?.statusText;

    // Determine error type based on status code
    let errorType: ErrorType;
    let severity: ToastSeverity = ToastSeverity.ERROR;

    if (!status) {
      // Network error or request timeout
      errorType = error.code === 'ECONNABORTED' ? ErrorType.TIMEOUT_ERROR : ErrorType.NETWORK_ERROR;
    } else if (status >= 400 && status < 500) {
      // Client errors
      if (status === 401) {
        errorType = ErrorType.AUTHENTICATION_ERROR;
      } else if (status === 403) {
        errorType = ErrorType.AUTHORIZATION_ERROR;
      } else if (status === 404) {
        errorType = ErrorType.NOT_FOUND_ERROR;
        severity = ToastSeverity.WARNING;
      } else if (status === 422) {
        errorType = ErrorType.VALIDATION_ERROR;
        severity = ToastSeverity.WARNING;
      } else {
        errorType = ErrorType.CLIENT_ERROR;
      }
    } else if (status >= 500) {
      // Server errors
      errorType = ErrorType.SERVER_ERROR;
    } else {
      errorType = ErrorType.UNKNOWN_ERROR;
    }

    // Get user-friendly message
    const message = this.getUserFriendlyMessage(status, errorType, error.message);

    // Determine auto-hide duration based on severity
    const autoHideDuration = severity === ToastSeverity.ERROR ? 8000 : 6000;

    return {
      id,
      type: errorType,
      code: status,
      message,
      originalError: error,
      timestamp,
      context: {
        ...context,
        url: error.config?.url,
        method: error.config?.method,
        statusText,
        responseData: error.response?.data,
      },
      severity,
      dismissible: true,
      autoHideDuration,
      // Add action for certain error types
      ...(errorType === ErrorType.AUTHENTICATION_ERROR && {
        actionLabel: 'LOGIN',
        onAction: () => {
          // This would trigger a login flow
          window.location.reload();
        },
      }),
      ...(errorType === ErrorType.NETWORK_ERROR && {
        actionLabel: 'RETRY',
        onAction: () => {
          // This could trigger a retry mechanism
          window.location.reload();
        },
      }),
    };
  }

  /**
   * Process standard JavaScript errors
   */
  private static processJavaScriptError(
    error: Error,
    id: string,
    timestamp: Date,
    context?: Record<string, unknown>
  ): AppError {
    let errorType: ErrorType = ErrorType.APPLICATION_ERROR;
    let severity: ToastSeverity = ToastSeverity.ERROR;

    // Categorize based on error name or message
    if (error.name === 'TypeError') {
      errorType = ErrorType.APPLICATION_ERROR;
    } else if (error.name === 'ReferenceError') {
      errorType = ErrorType.APPLICATION_ERROR;
    } else if (error.name === 'SyntaxError') {
      errorType = ErrorType.APPLICATION_ERROR;
    } else if (error.message.toLowerCase().includes('network')) {
      errorType = ErrorType.NETWORK_ERROR;
    } else if (error.message.toLowerCase().includes('timeout')) {
      errorType = ErrorType.TIMEOUT_ERROR;
    } else if (error.message.toLowerCase().includes('validation')) {
      errorType = ErrorType.VALIDATION_ERROR;
      severity = ToastSeverity.WARNING;
    }

    const message = this.getUserFriendlyMessage(undefined, errorType, error.message);

    return {
      id,
      type: errorType,
      message,
      originalError: error,
      timestamp,
      context: {
        ...context,
        errorName: error.name,
        stack: error.stack,
      },
      severity,
      dismissible: true,
      autoHideDuration: severity === ToastSeverity.ERROR ? 8000 : 6000,
    };
  }

  /**
   * Process custom error objects
   */
  private static processCustomError(
    error: unknown,
    id: string,
    timestamp: Date,
    context?: Record<string, unknown>
  ): AppError {
    const errorObj = error as Partial<{
      type: ErrorType;
      message: string;
      severity: ToastSeverity;
      code: number;
      dismissible: boolean;
      autoHideDuration: number;
    }>;

    const errorType: ErrorType = errorObj.type || ErrorType.APPLICATION_ERROR;
    const message = errorObj.message || DEFAULT_ERROR_MESSAGES[errorType];
    const severity = errorObj.severity || ToastSeverity.ERROR;

    return {
      id,
      type: errorType,
      code: errorObj.code,
      message: this.getUserFriendlyMessage(errorObj.code, errorType, message),
      originalError: error,
      timestamp,
      context,
      severity,
      dismissible: errorObj.dismissible ?? true,
      autoHideDuration:
        errorObj.autoHideDuration ?? (severity === ToastSeverity.ERROR ? 8000 : 6000),
    };
  }

  /**
   * Get user-friendly error message based on error code and type
   */
  private static getUserFriendlyMessage(
    code?: number,
    errorType?: ErrorType,
    originalMessage?: string
  ): string {
    // First, try to get message by error code
    if (code && ERROR_CODES[code as keyof typeof ERROR_CODES]) {
      return ERROR_CODES[code as keyof typeof ERROR_CODES];
    }

    // Then, try to get message by error type
    if (errorType && DEFAULT_ERROR_MESSAGES[errorType]) {
      return DEFAULT_ERROR_MESSAGES[errorType];
    }

    // For validation errors, try to extract useful info from original message
    if (errorType === ErrorType.VALIDATION_ERROR && originalMessage) {
      return `Validation failed: ${originalMessage}`;
    }

    // Fallback to original message if it's user-friendly (no stack traces, etc.)
    if (originalMessage && !originalMessage.includes('\n') && originalMessage.length < 200) {
      return originalMessage;
    }

    // Final fallback
    return DEFAULT_ERROR_MESSAGES[ErrorType.UNKNOWN_ERROR];
  }

  /**
   * Check if error is an Axios error
   */
  private static isAxiosError(error: unknown): error is AxiosError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'isAxiosError' in error &&
      (error as AxiosError).isAxiosError === true
    );
  }

  /**
   * Create a standardized error for form validation
   */
  static createValidationError(message: string, context?: Record<string, unknown>): AppError {
    const timestamp = new Date();
    const id = `validation-error-${timestamp.getTime()}`;

    return {
      id,
      type: ErrorType.VALIDATION_ERROR,
      message,
      timestamp,
      context,
      severity: ToastSeverity.WARNING,
      dismissible: true,
      autoHideDuration: 5000,
    };
  }

  /**
   * Create a standardized error for network issues
   */
  static createNetworkError(context?: Record<string, unknown>): AppError {
    const timestamp = new Date();
    const id = `network-error-${timestamp.getTime()}`;

    return {
      id,
      type: ErrorType.NETWORK_ERROR,
      message: DEFAULT_ERROR_MESSAGES[ErrorType.NETWORK_ERROR],
      timestamp,
      context,
      severity: ToastSeverity.ERROR,
      dismissible: true,
      autoHideDuration: 8000,
      actionLabel: 'RETRY',
      onAction: () => window.location.reload(),
    };
  }

  /**
   * Create a standardized error for authentication issues
   */
  static createAuthenticationError(context?: Record<string, unknown>): AppError {
    const timestamp = new Date();
    const id = `auth-error-${timestamp.getTime()}`;

    return {
      id,
      type: ErrorType.AUTHENTICATION_ERROR,
      message: DEFAULT_ERROR_MESSAGES[ErrorType.AUTHENTICATION_ERROR],
      timestamp,
      context,
      severity: ToastSeverity.ERROR,
      dismissible: true,
      autoHideDuration: 10000,
      actionLabel: 'LOGIN',
      onAction: () => window.location.reload(),
    };
  }

  /**
   * Simulates API calls with different error responses for demo and testing purposes
   *
   * @param errorType - The type of error to simulate
   * @param _context - Additional context for the error (currently unused)
   * @returns Promise that rejects with simulated API error
   */
  static async simulateApiError(
    errorType: 'validation' | 'network' | 'auth' | 'server' | 'notFound' | 'timeout' | 'forbidden',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _context?: Record<string, unknown>
  ): Promise<never> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    switch (errorType) {
      case 'validation': {
        // Simulate 422 Validation Error
        const validationError = new Error('Validation failed') as AxiosError;
        validationError.response = {
          status: 422,
          statusText: 'Unprocessable Entity',
          data: {
            message: 'Validation failed',
            errors: {
              accountName: ['Account name is required'],
              email: ['Please enter a valid email address'],
              costCenter: ['Cost center must be a valid number'],
              opportunityLeader: ['Opportunity leader is required'],
            },
          },
        } as AxiosError['response'];
        validationError.isAxiosError = true;
        validationError.config = {
          url: '/api/forms',
          method: 'POST',
        } as AxiosError['config'];
        throw validationError;
      }

      case 'network': {
        // Simulate Network Error
        const networkError = new Error('Network Error') as AxiosError;
        networkError.code = 'ECONNABORTED';
        networkError.isAxiosError = true;
        networkError.config = {
          url: '/api/forms',
          method: 'GET',
        } as AxiosError['config'];
        throw networkError;
      }

      case 'auth': {
        // Simulate 401 Authentication Error
        const authError = new Error('Unauthorized') as AxiosError;
        authError.response = {
          status: 401,
          statusText: 'Unauthorized',
          data: {
            message: 'Your session has expired. Please log in again.',
            error: 'AUTHENTICATION_REQUIRED',
          },
        } as AxiosError['response'];
        authError.isAxiosError = true;
        authError.config = {
          url: '/api/forms',
          method: 'POST',
        } as AxiosError['config'];
        throw authError;
      }

      case 'forbidden': {
        // Simulate 403 Forbidden Error
        const forbiddenError = new Error('Forbidden') as AxiosError;
        forbiddenError.response = {
          status: 403,
          statusText: 'Forbidden',
          data: {
            message: 'You do not have permission to perform this action.',
            error: 'INSUFFICIENT_PERMISSIONS',
          },
        } as AxiosError['response'];
        forbiddenError.isAxiosError = true;
        forbiddenError.config = {
          url: '/api/forms',
          method: 'DELETE',
        } as AxiosError['config'];
        throw forbiddenError;
      }

      case 'server': {
        // Simulate 500 Internal Server Error
        const serverError = new Error('Internal Server Error') as AxiosError;
        serverError.response = {
          status: 500,
          statusText: 'Internal Server Error',
          data: {
            message: 'An internal server error occurred. Please try again later.',
            error: 'INTERNAL_SERVER_ERROR',
            timestamp: new Date().toISOString(),
          },
        } as AxiosError['response'];
        serverError.isAxiosError = true;
        serverError.config = {
          url: '/api/forms',
          method: 'POST',
        } as AxiosError['config'];
        throw serverError;
      }

      case 'notFound': {
        // Simulate 404 Not Found Error
        const notFoundError = new Error('Not Found') as AxiosError;
        notFoundError.response = {
          status: 404,
          statusText: 'Not Found',
          data: {
            message: 'The requested resource could not be found.',
            error: 'RESOURCE_NOT_FOUND',
          },
        } as AxiosError['response'];
        notFoundError.isAxiosError = true;
        notFoundError.config = {
          url: '/api/forms/999',
          method: 'GET',
        } as AxiosError['config'];
        throw notFoundError;
      }

      case 'timeout': {
        // Simulate Request Timeout Error
        const timeoutError = new Error('Request Timeout') as AxiosError;
        timeoutError.code = 'ECONNABORTED';
        timeoutError.message = 'timeout of 5000ms exceeded';
        timeoutError.isAxiosError = true;
        timeoutError.config = {
          url: '/api/forms',
          method: 'POST',
          timeout: 5000,
        } as AxiosError['config'];
        throw timeoutError;
      }

      default: {
        // Unknown error type
        const unknownError = new Error('Unknown error type') as AxiosError;
        unknownError.response = {
          status: 500,
          statusText: 'Internal Server Error',
          data: {
            message: 'An unknown error occurred.',
            error: 'UNKNOWN_ERROR',
          },
        } as AxiosError['response'];
        unknownError.isAxiosError = true;
        throw unknownError;
      }
    }
  }

  /**
   * Extract validation errors from API response for form field mapping
   *
   * @param error - The processed error from API response
   * @returns Object mapping field names to error messages
   */
  static extractValidationErrors(error: AppError): Record<string, string> {
    const fieldErrors: Record<string, string> = {};
    const responseData = error.context?.responseData as Record<string, unknown> | undefined;

    if (responseData?.errors) {
      if (Array.isArray(responseData.errors)) {
        // Handle array format: [{ field: 'email', message: 'Invalid email' }]
        (responseData.errors as Array<unknown>).forEach((err: unknown) => {
          const errorObj = err as { field?: string; message?: string };
          if (errorObj.field && errorObj.message) {
            fieldErrors[errorObj.field] = errorObj.message;
          }
        });
      } else if (typeof responseData.errors === 'object') {
        // Handle object format: { email: ['Invalid email'], name: ['Required'] }
        const errorsObj = responseData.errors as Record<string, unknown>;
        Object.keys(errorsObj).forEach((field) => {
          const messages = errorsObj[field];
          if (Array.isArray(messages) && messages.length > 0) {
            fieldErrors[field] = messages[0];
          } else if (typeof messages === 'string') {
            fieldErrors[field] = messages;
          }
        });
      }
    }

    return fieldErrors;
  }
}
